import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { TIER } from '../utils/tier-constants';
import { PLANS, PaidTier, DurationMonths, getPlanPrice } from '../config/plans';
import { formatPrice } from '../utils/formatPrice';
import { PlanCard } from '../components/plans/PlanCard';
import { Accordion, Badge, Banner, Button, Checkbox, PageHeader, Section, SegmentedControl, Spinner } from '../components/ui';

type DurationOption = '1' | '3' | '6' | '12';

const DURATION_OPTIONS: { value: DurationOption; months: DurationMonths }[] = [
  { value: '1', months: 1 },
  { value: '3', months: 3 },
  { value: '6', months: 6 },
  { value: '12', months: 12 },
];

const TIER_RANK: Record<string, number> = { free: 0, pro: 1, premium: 2, admin: 3 };

// Rewritten in du-form from the pre-redesign FAQ (`git show 212fcf1:.../Upgrade.tsx`) — same
// questions and meaning, no new terms.
const FAQ: { question: string; answer: string }[] = [
  {
    question: 'Was passiert beim Upgrade von Pro auf Premium?',
    answer: 'Dein Premium-Zugang wird sofort aktiviert. Die verbleibende Pro-Laufzeit verfällt dabei.',
  },
  {
    question: 'Muss ich etwas kündigen?',
    answer:
      'Nein — es gibt keine automatische Verlängerung. Nach Ablauf der Laufzeit wird dein Account automatisch auf Free zurückgestuft. Du kannst dein Konto jederzeit in den Einstellungen vollständig löschen.',
  },
  {
    question: 'Welche Zahlungsmethoden werden akzeptiert?',
    answer: 'Alle gängigen Kreditkarten, SEPA-Lastschrift und weitere Zahlungsmethoden über Stripe.',
  },
];

/** Looks up the tier + duration for a `?plan=<planId>` deep link (e.g. from Telegram or UpgradeModal). */
function findPlanSelection(planId: string): { tier: PaidTier; months: DurationMonths } | null {
  const proMatch = PLANS.pro.prices.find((p) => p.planId === planId);
  if (proMatch) return { tier: 'pro', months: proMatch.months };
  const premiumMatch = PLANS.premium.prices.find((p) => p.planId === planId);
  if (premiumMatch) return { tier: 'premium', months: premiumMatch.months };
  return null;
}

const Upgrade: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user, isLoading: isAuthLoading } = useAuth();
  const userTier = user?.tier || TIER.FREE;

  // Parse ?plan= query param from Telegram deep links / UpgradeModal (e.g. ?plan=premium-6m):
  // preselects both the duration and which paid tier's card is highlighted.
  const planParam = searchParams.get('plan');
  const initialSelection = useMemo(() => {
    const found = planParam ? findPlanSelection(planParam) : null;
    return found ?? { tier: 'pro' as PaidTier, months: 3 as DurationMonths };
  }, [planParam]);

  const [monthsOption, setMonthsOption] = useState<DurationOption>(String(initialSelection.months) as DurationOption);
  const months = Number(monthsOption) as DurationMonths;
  const highlightedTier = initialSelection.tier;

  const [pendingTier, setPendingTier] = useState<PaidTier | null>(null);
  const [error, setError] = useState('');
  const [agreedToWaiver, setAgreedToWaiver] = useState(false);

  const handleUpgrade = async (tier: PaidTier) => {
    setPendingTier(tier);
    setError('');

    try {
      const { planId } = getPlanPrice(tier, months);
      const response = await api.post('/payment/create-checkout-session', {
        userId: user?.userId,
        planId,
        successUrl: `${window.location.origin}/dashboard/upgrade/success`,
        cancelUrl: `${window.location.origin}/dashboard/upgrade`,
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setPendingTier(null);
      }
    } catch (err: any) {
      setPendingTier(null);
      if (err.response?.status === 409 || err.response?.data?.code === 'ALREADY_SUBSCRIBED') {
        setError('Du hast bereits ein aktives Abo. Bitte warte, bis es abläuft, oder wähle ein höheres Paket.');
      } else {
        setError(err.response?.data?.message || 'Fehler beim Erstellen der Zahlung');
      }
    }
  };

  const isUnavailable = (tier: PaidTier) => TIER_RANK[userTier] >= TIER_RANK[tier];

  const durationLabel = months === 1 ? '1 Monat' : `${months} Monate`;

  /** "12 Monate Pro: 39,99 € einmalig (3,33 €/Monat). Es gibt keine automatische Verlängerung — ..." */
  const summaryFor = (tier: PaidTier) => {
    const { totalCents, perMonthCents } = getPlanPrice(tier, months);
    return `${durationLabel} ${PLANS[tier].name}: ${formatPrice(totalCents)} einmalig (${formatPrice(perMonthCents, { perMonth: true })}). Es gibt keine automatische Verlängerung — nach Ablauf wirst du automatisch auf Free zurückgestuft.`;
  };

  const ctaLabelFor = (tier: PaidTier) => `${PLANS[tier].name} für ${formatPrice(getPlanPrice(tier, months).totalCents)} kaufen`;

  return (
    <div className="mx-auto w-full max-w-wide">
      <PageHeader title="Plan wählen" description="Mehr Suchen, Filter und schnellere Meldungen." />

      {isAuthLoading ? (
        <div className="mt-8 flex justify-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <SegmentedControl
              label="Laufzeit"
              showLabel
              value={monthsOption}
              onChange={setMonthsOption}
              options={DURATION_OPTIONS.map(({ value, months: optionMonths }) => ({
                value,
                label: optionMonths === 1 ? '1 Monat' : `${optionMonths} Monate`,
                badge:
                  optionMonths === 12 ? (
                    <Badge tone="signal" className="px-1.5 py-0.5 text-[11px] shadow-card">
                      Bester Wert
                    </Badge>
                  ) : undefined,
                ariaLabel: optionMonths === 12 ? '12 Monate, bester Wert' : undefined,
              }))}
            />
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <PlanCard plan={PLANS.free} current={userTier === TIER.FREE} />
            <PlanCard
              plan={PLANS.pro}
              months={months}
              highlighted={highlightedTier === 'pro'}
              current={userTier === TIER.PRO}
            />
            <PlanCard
              plan={PLANS.premium}
              months={months}
              highlighted={highlightedTier === 'premium'}
              current={userTier === TIER.PREMIUM}
            />
          </div>

          <div className="mt-6">
            <Checkbox
              checked={agreedToWaiver}
              onChange={(e) => setAgreedToWaiver(e.target.checked)}
              disabled={pendingTier !== null}
              label={
                <span className="text-base text-ink">
                  Ich verlange und stimme ausdrücklich zu, dass vor Ablauf der Widerrufsfrist mit der Ausführung der
                  Dienstleistung begonnen wird. Mir ist bekannt, dass ich bei vollständiger Vertragserfüllung mein
                  Widerrufsrecht verliere und bei einem Widerruf vor Ablauf der gebuchten Laufzeit Wertersatz für die
                  bis dahin erbrachten Leistungen schulde. Die{' '}
                  <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="underline">
                    Widerrufsbelehrung und das Muster-Widerrufsformular
                  </a>{' '}
                  sowie die{' '}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline">
                    AGB
                  </a>{' '}
                  habe ich zur Kenntnis genommen.
                </span>
              }
            />
            {!agreedToWaiver && <p className="mt-2 text-sm text-muted">Bitte bestätige zuerst die Einwilligung.</p>}
          </div>

          {error && (
            <Banner tone="danger" className="mt-4">
              {error}
            </Banner>
          )}

          {/* § 312j: each paid tier's summary sits directly above its own CTA on every
              viewport (one grid, one block per tier), not in two separately-stacked grids. */}
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <div />
            {(['pro', 'premium'] as const).map((tier) => (
              <div key={tier} className="flex flex-col gap-2">
                <p className="text-sm text-muted">{summaryFor(tier)}</p>
                <Button
                  fullWidth
                  disabled={!agreedToWaiver || pendingTier !== null || isUnavailable(tier)}
                  loading={pendingTier === tier}
                  onClick={() => handleUpgrade(tier)}
                >
                  {ctaLabelFor(tier)}
                </Button>
              </div>
            ))}
          </div>

          <Section title="Häufige Fragen" className="mt-8">
            <div>
              {FAQ.map((item, index) => (
                <Accordion key={item.question} title={item.question} defaultOpen={index === 0}>
                  <p>{item.answer}</p>
                </Accordion>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default Upgrade;
